import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
    GetInvoiceDetailRequest,
    GetInvoiceDetailResponse,
    GetInvoiceDetailService,
    ImageViewableStatus
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    DateFormat,
    forLifeOf,
    getDynamicRouteLink,
    LifecycleAware
} from '@lsq-frontend/common-ui';
import { getRouterLinkUrlById, Icons } from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { PopoverPlacement } from 'ngx-smart-popover';
import { atlas, CUSTOMER_OVERVIEW } from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { InvoiceHistoricalImagePreviewModal } from '../invoices.models';
import { InvoiceDetailI18n } from './invoice-detail.i18n';

@LifecycleAware()
@Component({
    selector: 'invoice-detail',
    template: `
        <div *ngIf="initializing">
            <lsq-circle-loader></lsq-circle-loader>
        </div>
        <div *ngIf="!initializing">
            <lsq-banner
                [titleText]="i18n.BannerHeader"
                [subTitle]="lastUpdatedDate | lastUpdatedDate"
            >
            </lsq-banner>
            <div class="invoice-details page-container container-fluid">
                <div class="row">
                    <div class="col-12">
                        <div class="row">
                            <div class="col-md-6">
                                <div class="invoice-header">
                                    <a
                                        class="font-weight-bold debtor-name"
                                        [routerLink]="getOverviewLink(invoice.debtorKey)"
                                    >
                                        {{ invoice.debtorName }}
                                    </a>
                                    <h2 class="invoice-number">
                                        {{ i18n.InvoiceNumber }} {{ invoice.invoiceNumber }}
                                        <a
                                            *ngIf="isInvoiceImageViewable"
                                            class="invoice-image-preview-btn"
                                            href="javascript:void(0)"
                                            (click)="showInvoicePreviewModal()"
                                        >
                                            <lsq-icon
                                                [type]="icons.ImageFile"
                                                [width]="14"
                                                [height]="18"
                                            >
                                            </lsq-icon>
                                        </a>
                                    </h2>
                                    <h3 class="invoice-amount">
                                        {{ invoice.invoiceAmount | currency }}
                                    </h3>
                                </div>
                                <div class="invoice-data-row row">
                                    <div class="col-4">
                                        <label for="invoiceDate">{{ i18n.InvoiceDate }}</label>
                                        <h3 id="invoiceDate">
                                            {{
                                                invoice.invoiceDate
                                                    ? (invoice.invoiceDate | date: shortDateFormat)
                                                    : i18n.NullValue
                                            }}
                                        </h3>
                                    </div>
                                    <div class="col-4">
                                        <label for="dueDate">{{ i18n.DueDate }}</label>
                                        <h3 id="dueDate">
                                            {{
                                                invoice.dueDate
                                                    ? (invoice.dueDate | date: shortDateFormat)
                                                    : i18n.NullValue
                                            }}
                                        </h3>
                                    </div>
                                    <div class="col-4">
                                        <label for="purchaseDate">{{ i18n.PurchaseDate }}</label>
                                        <h3 id="purchaseDate">
                                            {{
                                                invoice.purchaseDate
                                                    ? (invoice.purchaseDate | date: shortDateFormat)
                                                    : i18n.NullValue
                                            }}
                                        </h3>
                                    </div>
                                </div>
                                <div class="invoice-data-row row">
                                    <div class="col-4">
                                        <label for="terms">{{ i18n.Terms }}</label>
                                        <h3 id="terms">
                                            {{
                                                invoice.terms
                                                    ? invoice.terms + i18n.Days
                                                    : i18n.NullValue
                                            }}
                                        </h3>
                                    </div>
                                    <div class="col-4">
                                        <label
                                            for="daysPastDue"
                                            [popover]="i18n.DaysPastDueToolTip"
                                        >
                                            {{ i18n.DaysPastDue }}
                                        </label>
                                        <h3 id="daysPastDue">
                                            {{
                                                invoice.daysPastDue && invoice.daysPastDue > 0
                                                    ? invoice.daysPastDue + i18n.Days
                                                    : i18n.NullValue
                                            }}
                                        </h3>
                                    </div>
                                    <div class="col-4">
                                        <label for="age" [popover]="i18n.AgeToolTip">
                                            {{ i18n.Age }}
                                        </label>
                                        <h3 id="age">
                                            {{
                                                invoice.age
                                                    ? invoice.age + i18n.Days
                                                    : i18n.NullValue
                                            }}
                                        </h3>
                                    </div>
                                </div>
                                <div class="invoice-data-row row">
                                    <div class="col-4">
                                        <label
                                            for="balanceDue"
                                            [popover]="i18n.BalanceDueToolTip"
                                            [popoverPlacement]="placement.TopRight"
                                        >
                                            {{ i18n.BalanceDue }}
                                        </label>
                                        <h3 id="balanceDue">
                                            {{
                                                invoice.balanceDue
                                                    ? (invoice.balanceDue | currency)
                                                    : i18n.NullValue
                                            }}
                                        </h3>
                                    </div>
                                    <div class="col-4">
                                        <label
                                            for="partialPayment"
                                            [popover]="i18n.PartialPaymentToolTip"
                                        >
                                            {{ i18n.PartialPayment }}
                                        </label>
                                        <h3 id="partialPayment">
                                            {{
                                                invoice.partialPayment
                                                    ? (invoice.partialPayment | currency)
                                                    : i18n.NullValue
                                            }}
                                        </h3>
                                    </div>
                                    <div class="col-4">
                                        <label for="lastPaymentDate">{{
                                            i18n.LastPaymentDate
                                        }}</label>
                                        <h3 id="lastPaymentDate">
                                            {{
                                                invoice.lastPaymentDate
                                                    ? (invoice.lastPaymentDate
                                                      | date: shortDateFormat)
                                                    : i18n.NullValue
                                            }}
                                        </h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `,
    styleUrls: ['./invoice-detail.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class InvoiceDetailComponent implements OnInit, OnDestroy {
    public invoice: GetInvoiceDetailResponse;
    public lastUpdatedDate: Date;
    public shortDateFormat = DateFormat.LsqShortYear;
    public i18n = InvoiceDetailI18n;
    public initializing = true;
    public request: GetInvoiceDetailRequest;
    public placement = PopoverPlacement;
    public icons = Icons;

    constructor(
        public getInvoiceDetailService: GetInvoiceDetailService,
        private _activatedRoute: ActivatedRoute,
        private _modalService: ModalService
    ) {}

    public ngOnInit(): void {
        this._activatedRoute.params.pipe(forLifeOf(this)).subscribe((params) => {
            this.request = new GetInvoiceDetailRequest({
                invoiceKey: +params.invoiceKey
            });
            this.getInvoiceDetailService.execute(this.request);
        });

        this.getInvoiceDetailService.dataStream.pipe(forLifeOf(this)).subscribe((response) => {
            this.lastUpdatedDate = response.lastUpdatedDate;
            this.invoice = response;
            this.initializing = false;
        });
    }

    public ngOnDestroy(): void {}

    public getOverviewLink(debtorKey: number): string {
        return getDynamicRouteLink(getRouterLinkUrlById(atlas, CUSTOMER_OVERVIEW), { debtorKey });
    }

    public showInvoicePreviewModal(): void {
        this._modalService.showModal(new InvoiceHistoricalImagePreviewModal(this.invoice));
    }

    public get isInvoiceImageViewable(): boolean {
        return this.invoice.imageViewableStatus === ImageViewableStatus.Viewable;
    }
}
